import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import { TelephonyDeploymentResponse } from '../../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import { voiceOptions } from '../../../../../shared/constants/sparkConstants/voiceOptions';
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from '../../../../../shared/constants/featureFlagSettingIds';
import { GenAIFoodOrdering } from '../../../../../shared/constants/featureFlagIds';

export const buildTelephonyUpdateRequest = (
    editingDeployment: TelephonyDeploymentResponse,
    editType: string,
    editValues: { editAsrProvider?: string, editProactivePrompt?: string, editVoiceString?: string }
): any => {
    let request: any = { ...editingDeployment };

    if (editType === 'asr') {
        request.asrProvider = editValues.editAsrProvider;
    } else if (editType === 'proactive') {
        request.generativeProactiveResponsePrompt = editValues.editProactivePrompt;
        request.useGenerativeProactiveResponse = true;
        request.useProactiveResponses = true;
    } else if (editType === 'voice') {
        const standardOption = voiceOptions.find(o => o.voice === editValues.editVoiceString);
        const provider = standardOption ? standardOption.provider : 'ElevenLabs';

        request.ttsProvider = provider;
        request.ttsVoice = editValues.editVoiceString;
        request.ttsOptions = [{ ttsProvider: provider, ttsVoice: editValues.editVoiceString }];
    }

    return request;
};

export const buildBulkVoiceUpdateRequest = (
    dep: TelephonyDeploymentResponse,
    value: any
): any => {
    let request: any = { ...dep };
    if (value && typeof value === 'object' && value.isCustom) {
        const isSpanish = (dep.locale || '').toLowerCase().startsWith('es');
        const voiceStr = isSpanish ? value.spanish : value.english;
        request.ttsVoice = voiceStr;
        request.ttsProvider = voiceStr ? 'ElevenLabs' : 'Deepgram';
        request.ttsOptions = [{ ttsProvider: request.ttsProvider, ttsVoice: voiceStr }];
    } else {
        const standardOption = voiceOptions.find(o => o.voice === value);
        const provider = standardOption ? standardOption.provider : (value ? 'ElevenLabs' : 'Deepgram');
        request.ttsVoice = value;
        request.ttsProvider = provider;
        request.ttsOptions = [{ ttsProvider: provider, ttsVoice: value }];
    }
    return request;
};

export const buildBulkLiveUpdateRequest = (app: ApplicationModel, value: any): ApplicationModel => {
    let newTargetLaunchDate = app.targetLaunchDate;
    let nextLive = false;
    
    if (typeof value === 'object' && value !== null) {
        nextLive = value.status === 'live';
        if (value.status === 'draft' && value.targetLaunchDate && value.targetLaunchDate.length === 10) {
            const parts = value.targetLaunchDate.split('/');
            if (parts.length === 3) {
                newTargetLaunchDate = new Date(`${parts[2]}-${parts[0]}-${parts[1]}T00:00:00Z`).toISOString();
            }
        }
    } else {
        nextLive = value === 'live';
    }
    
    return {
        ...app,
        isLive: nextLive,
        targetLaunchDate: newTargetLaunchDate
    };
};

export const buildFeatureFlagUpdateRequest = (
    appFlag: any,
    masterFlag: any,
    modelValue?: string,
    promptValue?: string
): any => {
    const mergedSettings = (masterFlag.featureFlagSettings || []).map((masterSetting: any) => {
        const matchedAppSetting = appFlag?.featureFlagSettings?.find((s: any) => s.id === masterSetting.id);
        let v = matchedAppSetting?.value || masterSetting.value || '';
        if (masterSetting.id === FoodOrderingGenAIModelSettingId) {
            if (modelValue) v = modelValue;
        } else if (masterSetting.id === FoodOrderingGenAIPromptOverrideSettingId) {
            if (promptValue !== undefined) v = promptValue;
            if (v === '{{constant.base_ordering_prompt}}') {
                v = '{{constant.new_ordering_prompt}}';
            }
        }
        return { ...masterSetting, value: v };
    });
    
    return {
        featureFlagId: GenAIFoodOrdering,
        featureFlagSettings: mergedSettings
    };
};

export const buildClosedDaysUpdateRequest = (existingRaw: string | undefined | null, newDates: string[]): string => {
    let finalValue = existingRaw || 'none';
    if (newDates.length > 0) {
        const existingDatesList = existingRaw
            ? existingRaw.split(',').map(p => p.trim()).filter(Boolean).filter(p => p.toLowerCase() !== 'none')
            : [];
        const combinedDates = Array.from(new Set([...existingDatesList, ...newDates]));
        finalValue = combinedDates.join(', ');
    }
    return finalValue;
};
